
import React, { useEffect, useState, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import ImageComparisonSlider from "./ImageComparisonSlider";
import { isBrowser } from "@/utils/runtime";

type GalleryImage = {
  src: string;
  alt: string;
  realtor: string;
  location: string;
};

const galleryImages: GalleryImage[] = [
  {
    src: "https://images.unsplash.com/photo-1524230572899-a752b3835840",
    alt: "Sala de estar moderna com sofá cinza",
    realtor: "Ana Silva",
    location: "Itapema/SC"
  },
  {
    src: "https://images.unsplash.com/photo-1493962853295-0fd70327578a",
    alt: "Cozinha planejada com ilha central",
    realtor: "Carlos Mendes",
    location: "São Paulo/SP"
  },
  {
    src: "https://images.unsplash.com/photo-1472396961693-142e6e269027",
    alt: "Quarto com vista para o mar",
    realtor: "Mariana Costa",
    location: "Florianópolis/SC"
  },
  {
    src: "https://images.unsplash.com/photo-1466721591366-2d5fba72006d",
    alt: "Área externa com piscina",
    realtor: "Paulo Mendonça",
    location: "Rio de Janeiro/RJ"
  },
  {
    src: "https://images.unsplash.com/photo-1721322800607-8c38375eef04",
    alt: "Apartamento moderno com iluminação natural",
    realtor: "Luiza Fernandes",
    location: "Belo Horizonte/MG"
  },
  {
    src: "https://images.unsplash.com/photo-1492321936769-b49830bc1d1e",
    alt: "Condomínio de alto padrão com vista panorâmica",
    realtor: "Ricardo Oliveira",
    location: "Recife/PE"
  }
];

// Images for the comparison slider
const comparisonImages = {
  before: "https://images.unsplash.com/photo-1524230572899-a752b3835840?q=80&auto=format&fit=crop&w=1000",
  after: "https://images.unsplash.com/photo-1524230572899-a752b3835840?q=100&auto=format&fit=crop&w=1000"
};

const AIGallery = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  // Auto rotation for carousel
  useEffect(() => {
    if (!isBrowser) return;
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setActiveIndex(prev => (prev + 1) % galleryImages.length);
      }, 3000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, galleryImages.length]);

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  return (
    <section className="py-16 md:py-24 bg-toctoc-beige">
      <div className="container max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-toctoc-blue-real mb-4">
            Veja o que outros corretores já criaram com a IA Toc Toc
          </h2>
          <p className="text-xl text-toctoc-gray-dark max-w-2xl mx-auto">
            Imagens reais editadas automaticamente pela nossa IA e utilizadas em anúncios de sucesso
          </p>
        </div>

        <div className="relative px-4 md:px-12" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <Carousel className="w-full" 
            opts={{
              align: "start",
              loop: true,
            }}
          >
            <CarouselContent>
              {galleryImages.map((image, index) => (
                <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                  <div className="p-2">
                    <div className="rounded-xl overflow-hidden border border-gray-200 shadow-md bg-white h-full flex flex-col transform transition-transform hover:scale-105 duration-300">
                      <div className="relative aspect-video">
                        <img
                          src={image.src}
                          alt={image.alt}
                          className="w-full h-full object-cover"
                        />
                        <Badge className="absolute top-2 right-2 bg-toctoc-orange border-none text-white">
                          100% IA Toc Toc
                        </Badge>
                      </div>
                      <div className="p-4 text-center">
                        <p className="text-toctoc-gray-medium font-medium">
                          Foto enviada por {image.realtor}
                        </p>
                        <p className="text-toctoc-gray-dark text-sm">
                          {image.location}
                        </p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="hidden md:block">
              <CarouselPrevious className="left-0" />
              <CarouselNext className="right-0" />
            </div>
          </Carousel>
        </div>

        <div className="mt-16 max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 bg-toctoc-blue-real text-white text-center">
            <h3 className="text-2xl font-bold mb-2">
              Veja a transformação que nossa IA pode fazer
            </h3>
            <p className="text-white/80">
              Arraste o slider para comparar antes e depois
            </p>
          </div>
          <div className="h-[400px] md:h-[500px]">
            <ImageComparisonSlider
              beforeImage={comparisonImages.before}
              afterImage={comparisonImages.after}
              beforeLabel="Antes"
              afterLabel="Depois"
            />
          </div>
        </div>

        <div className="mt-12 text-center">
          <div className="bg-white rounded-lg p-8 shadow-lg max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-toctoc-gray-dark mb-4">
              Imaginou isso nos seus anúncios?
            </h3>
            <p className="text-lg text-toctoc-gray-medium mb-6">
              Experimente gratuitamente e veja como sua vitrine pode se transformar com a IA Toc Toc.
            </p>
            <button className="bg-toctoc-orange hover:bg-opacity-90 text-white font-semibold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center justify-center mx-auto">
              🟠 Testar Agora com Minha Foto
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIGallery;
